import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './core/services/auth.service';
import { IdleService } from './core/services/idle.service';
import { CartService } from './features/inventory/services/cart.service';
import { NotificationStateService } from './core/services/notification-state.service';
import { NotificationDialogComponent } from './features/notifications/notification-dialog/notification-dialog.component';
import { CartDialogComponent } from './features/inventory/components/cart/cart-dialog.component';
import { ProfileComponent } from './features/profile/components/profile/profile.component';
import { UploadService } from './features/inventory/services/upload.service';
import { environment } from '@environments/environment';
import { ConnectivityService } from './core/services/connectivity.service';

@Component({
  selector: 'app-root',
  template: `
    <div [class]="theme">
      <ng-container *ngIf="authService.isAuthenticated(); else loginTemplate">
        <mat-toolbar>
          <button
            mat-icon-button
            (click)="sidenav.toggle()"
          >
            <mat-icon>menu</mat-icon>
          </button>
          <div class="logo-container">
            <img
              src="assets/logo.png"
              alt="TCT Logo"
              class="logo"
            />
          </div>
          <span class="title">TCT System</span>
          <span class="spacer"></span>

                    <!-- Download button -->
          <button
            mat-icon-button
            class="download-btn android-btn"
            (click)="downloadAndroidApp()"
            matTooltip="Download Android Tablet App"
          >
            <mat-icon>android</mat-icon>
          </button>

          <button
            mat-icon-button
            class="notification-btn"
            [matBadge]="notificationCount$ | async"
            matBadgeColor="warn"
            [matBadgeHidden]="(notificationCount$ | async) === 0"
            (click)="openNotifications()"
          >
            <mat-icon>notifications</mat-icon>
          </button>
          
          <button
            mat-icon-button
            [matMenuTriggerFor]="userMenu"
          >
            <mat-icon>account_circle</mat-icon>
          </button>
          <mat-menu #userMenu="matMenu">
            <button
              mat-menu-item
              (click)="openProfile()"
            >
              <mat-icon>person</mat-icon>
              <span>Profile</span>
            </button>
            <mat-divider></mat-divider>
            <button
              mat-menu-item
              (click)="logout()"
            >
              <mat-icon>exit_to_app</mat-icon>
              <span>Logout</span>
            </button>
          </mat-menu>
        </mat-toolbar>

        <div
          *ngIf="(isOffline$ | async) || (isApiUnreachable$ | async)"
          class="connection-banner"
          [class.offline]="(isOffline$ | async)"
          [class.unreachable]="(isApiUnreachable$ | async)"
        >
          <mat-icon class="banner-icon">cloud_off</mat-icon>
          <span>
            {{ (isOffline$ | async) ? 'No internet connection. Some actions may not work.' : 'Cannot reach server. Retrying…' }}
          </span>
        </div>

        <mat-sidenav-container>
          <mat-sidenav
            #sidenav
            mode="side"
            opened
            class="app-sidenav"
          >
            <mat-nav-list>
              <a
                mat-list-item
                routerLink="/users"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>people</mat-icon>
                <span matListItemTitle>Users</span>
              </a>
              <a
                mat-list-item
                routerLink="/item-master"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>category</mat-icon>
                <span matListItemTitle>Item Master</span>
              </a>              
              <a
                mat-list-item
                routerLink="/inventory"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>inventory_2</mat-icon>
                <span matListItemTitle>Inventory</span>
              </a>
              <a
                mat-list-item
                routerLink="/workflow"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>account_tree</mat-icon>
                <span matListItemTitle>Workflow</span>
              </a>
              <a
                mat-list-item
                routerLink="/jobs"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>work</mat-icon>
                <span matListItemTitle>Jobs</span>
              </a>
              <a
                mat-list-item
                routerLink="/customers"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>supervised_user_circle</mat-icon>
                <span matListItemTitle>Customers</span>
              </a>
              <a
                mat-list-item
                routerLink="/suppliers"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>business</mat-icon>
                <span matListItemTitle>Suppliers</span>
              </a>
              <a
                mat-list-item
                routerLink="/reports"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>assessment</mat-icon>
                <span matListItemTitle>Reports</span>
              </a>
              <a
                mat-list-item
                routerLink="/settings"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>settings</mat-icon>
                <span matListItemTitle>Settings</span>
              </a>
              <a
                mat-list-item
                routerLink="/audit-logs"
                routerLinkActive="active"
              >
                <mat-icon matListItemIcon>history</mat-icon>
                <span matListItemTitle>Audit Logs</span>
              </a>
            </mat-nav-list>
          </mat-sidenav>

          <mat-sidenav-content>
            <div class="content">
              <router-outlet></router-outlet>
            </div>
            <div class="footer">v{{ version }}</div>
          </mat-sidenav-content>
        </mat-sidenav-container>
      </ng-container>

      <ng-template #loginTemplate>
        <router-outlet></router-outlet>
      </ng-template>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100vh;
      }

      .logo-container {
        display: flex;
        align-items: center;
        margin-right: 16px;
        height: 40px;
        width: 60px;
      }

      .logo {
        height: 100%;
        width: 100%;
        object-fit: contain;
      }

      .title {
        font-size: 20px;
        font-weight: 500;
        letter-spacing: 0.5px;
      }

      .spacer {
        flex: 1 1 auto;
      }

      mat-sidenav-container {
        height: calc(100% - 64px);
      }

      .app-sidenav {
        padding-top: 16px;
      }

      .content {
        padding: 24px;
        background-color: #f8f9fa;
        min-height: 100%;
        padding-left: 0;
      }

      .notification-btn {
        --mat-badge-container-overlap-offset: -20px;
        margin-right: 8px;
      }

      .cart-btn {
        margin-right: 8px;
      }

      .download-btn {
        margin-right: 4px;
        transition: all 0.3s ease;
      }

      .download-btn:hover {
        background-color: rgba(0, 0, 0, 0.1);
        transform: scale(1.05);
      }

      .android-btn mat-icon {
        color: #a4c639;
      }

      .android-btn:hover mat-icon {
        color: #8bc34a;
      }

      .ios-btn mat-icon {
        color: #007aff;
      }

      .ios-btn:hover mat-icon {
        color: #0051d0;
      }

      mat-toolbar {
        position: relative;
        z-index: 2;
      }

      .mat-icon-button {
        margin-left: 8px;
      }

      .mdc-list-item {
        padding-right: 36px;
      }

      .footer {
        padding: 8px 16px;
        color: #666;
        font-size: 12px;
        text-align: right;
      }

      .connection-banner {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 16px;
        font-size: 14px;
        color: #fff;
      }

      .connection-banner.offline {
        background-color: #d32f2f; /* red */
      }

      .connection-banner.unreachable {
        background-color: #f57c00; /* orange */
      }

      .banner-icon {
        color: #fff;
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  theme = 'light-theme';
  cartItemCount$: Observable<number>;
  notificationCount$: Observable<number>;
  version = environment.version;
  isOffline$: Observable<boolean>;
  isApiUnreachable$: Observable<boolean>;

  constructor(
    private dialog: MatDialog,
    public authService: AuthService,
    private router: Router,
    private cartService: CartService,
    private notificationStateService: NotificationStateService,
    private uploadService: UploadService,
    private idleService: IdleService, // Inject IdleService to initialize
    private connectivity: ConnectivityService,
  ) {
    this.cartItemCount$ = this.cartService
      .getCartSummary()
      .pipe(map((summary) => summary.totalItems));

    this.notificationCount$ = this.notificationStateService.totalNotificationCount$;

    this.isOffline$ = this.connectivity.isOffline$;
    this.isApiUnreachable$ = this.connectivity.isApiUnreachable$;
  }

  ngOnInit() {
    // NotificationStateService automatically handles refresh with timer
  }

  toggleTheme() {
    this.theme = this.theme === 'light-theme' ? 'dark-theme' : 'light-theme';
  }

  openNotifications() {
    this.dialog.open(NotificationDialogComponent, {
      width: '600px',
    });
  }

  openCart() {
    this.dialog.open(CartDialogComponent, {
      width: '800px',
    });
  }

  logout() {
    this.authService.logout();
  }

  openProfile() {
    this.dialog.open(ProfileComponent, {
      width: '90vw',
      maxWidth: '600px',
      height: 'auto',
      maxHeight: '90vh',
      panelClass: 'responsive-dialog'
    });
  }

  downloadAndroidApp() {
    this.uploadService.getLatestLink().subscribe({
      next: (links) => {
        if (links.android) {
          window.open(links.android, '_blank');
        } else {
          console.error('Android download link not available');
        }
      },
      error: (error) => {
        console.error('Error getting download links:', error);
      }
    });
  }

  downloadIosApp() {
    this.uploadService.getLatestLink().subscribe({
      next: (links) => {
        if (links.ios) {
          window.open(links.ios, '_blank');
        } else {
          console.error('iOS download link not available');
        }
      },
      error: (error) => {
        console.error('Error getting download links:', error);
      }
    });
  }
}
